import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 20, right: 140, bottom: 60, left: 60 };
const CHART_HEIGHT = 420;
const SIDE_PANEL_WIDTH = 100;
const SIDE_PANEL_GAP = 20;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  scrollContainer: {
    overflowX: 'auto',
    overflowY: 'hidden',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '10px 14px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '240px',
  },
  legend: {
    marginTop: '12px',
    display: 'flex',
    gap: '16px',
    fontSize: '11px',
    color: '#9ca3af',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  peakBadge: {
    display: 'inline-block',
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: '4px',
    padding: '2px 8px',
    fontSize: '11px',
    fontWeight: 600,
    color: '#1e40af',
    marginLeft: '8px',
  },
};

function formatDate(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

export default function WaveletScalogram({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.dates || !data.scales || !data.power || data.power.length === 0) return;

    const { dates, scales, power, coneOfInfluence, globalSpectrum, peakScale } = data;

    const nDates = dates.length;
    const nScales = scales.length;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 800;
    const width = Math.max(700, containerWidth);
    const heatmapRight = width - MARGIN.right;
    const innerW = heatmapRight - MARGIN.left;
    const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    // Side panel sits to the right of the heatmap
    const sidePanelLeft = heatmapRight + SIDE_PANEL_GAP;
    const sidePanelRight = sidePanelLeft + SIDE_PANEL_WIDTH;
    const totalWidth = sidePanelRight + 10;

    svg.attr('width', totalWidth).attr('height', CHART_HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // ------------------------------------------------------------------
    // Scales
    // ------------------------------------------------------------------
    const x = d3.scaleLinear()
      .domain([0, nDates - 1])
      .range([0, innerW]);

    // Log scale for y-axis (wavelet scales) — inverted so small scales (high freq) at top
    const yDomain = [d3.min(scales), d3.max(scales)];
    const y = d3.scaleLog()
      .domain(yDomain)
      .range([0, innerH]);

    // Color scale for wavelet power
    const allPower = power.flat();
    const powerMax = d3.max(allPower) || 1;
    const colorScale = d3.scaleSequential()
      .domain([0, powerMax])
      .interpolator(d3.interpolateBlues);

    // Cell dimensions
    const cellW = innerW / nDates;

    // ------------------------------------------------------------------
    // Heatmap cells
    // ------------------------------------------------------------------
    for (let si = 0; si < nScales; si++) {
      const scale = scales[si];
      const nextScale = si < nScales - 1 ? scales[si + 1] : scale * (scale / (si > 0 ? scales[si - 1] : scale / 2));

      const yTop = y(scale);
      const yBottom = si < nScales - 1 ? y(scales[si + 1]) : innerH;
      const cellH = yBottom - yTop;

      for (let di = 0; di < nDates; di++) {
        const val = power[si][di];
        const cx = di * cellW;

        g.append('rect')
          .attr('x', cx)
          .attr('y', yTop)
          .attr('width', cellW + 0.5) // slight overlap to avoid gaps
          .attr('height', cellH + 0.5)
          .attr('fill', colorScale(val))
          .attr('stroke', 'none')
          .style('cursor', 'pointer')
          .on('mousemove', (event) => {
            setTooltip({
              x: event.clientX + 12,
              y: event.clientY - 10,
              date: dates[di],
              scale: scale,
              period: scale,
              power: val,
            });
          })
          .on('mouseleave', () => {
            setTooltip(null);
          });
      }
    }

    // ------------------------------------------------------------------
    // Cone of Influence boundary
    // ------------------------------------------------------------------
    if (coneOfInfluence && coneOfInfluence.length === nDates) {
      const coiPoints = [];
      for (let di = 0; di < nDates; di++) {
        const coiScale = coneOfInfluence[di];
        // Clamp to scale domain
        const clampedScale = Math.max(yDomain[0], Math.min(yDomain[1], coiScale));
        coiPoints.push({ x: x(di), y: y(clampedScale) });
      }

      const coiLine = d3.line()
        .x((d) => d.x)
        .y((d) => d.y)
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(coiPoints)
        .attr('d', coiLine)
        .attr('fill', 'none')
        .attr('stroke', '#fff')
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '6,4')
        .attr('stroke-opacity', 0.9);

      // Semi-transparent overlay for unreliable region (below COI line)
      const coiArea = d3.area()
        .x((d) => d.x)
        .y0((d) => d.y)
        .y1(innerH)
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(coiPoints)
        .attr('d', coiArea)
        .attr('fill', '#fff')
        .attr('fill-opacity', 0.3)
        .attr('stroke', 'none')
        .style('pointer-events', 'none');
    }

    // ------------------------------------------------------------------
    // Peak scale annotation
    // ------------------------------------------------------------------
    if (peakScale != null && peakScale >= yDomain[0] && peakScale <= yDomain[1]) {
      const peakY = y(peakScale);

      g.append('line')
        .attr('x1', 0)
        .attr('x2', innerW)
        .attr('y1', peakY)
        .attr('y2', peakY)
        .attr('stroke', '#fbbf24')
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '4,3')
        .attr('stroke-opacity', 0.8);

      g.append('text')
        .attr('x', innerW - 4)
        .attr('y', peakY - 6)
        .attr('text-anchor', 'end')
        .attr('font-size', '9px')
        .attr('font-weight', 600)
        .attr('fill', '#fbbf24')
        .text(`Peak scale: ${peakScale}`);
    }

    // ------------------------------------------------------------------
    // X axis (dates)
    // ------------------------------------------------------------------
    const maxXTicks = Math.min(nDates, Math.floor(innerW / 50));
    const xTickStep = Math.max(1, Math.ceil(nDates / maxXTicks));
    const xTickIndices = [];
    for (let i = 0; i < nDates; i += xTickStep) {
      xTickIndices.push(i);
    }

    const xAxis = g.append('g').attr('transform', `translate(0, ${innerH})`);
    xTickIndices.forEach((i) => {
      const xPos = x(i);
      xAxis.append('line')
        .attr('x1', xPos)
        .attr('x2', xPos)
        .attr('y1', 0)
        .attr('y2', 5)
        .attr('stroke', '#e5e7eb');
      xAxis.append('text')
        .attr('x', xPos)
        .attr('y', 18)
        .attr('text-anchor', 'middle')
        .attr('font-size', '9px')
        .attr('fill', '#6b7280')
        .text(formatDate(dates[i]));
    });

    // X axis baseline
    xAxis.append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', 0)
      .attr('y2', 0)
      .attr('stroke', '#e5e7eb');

    // X axis label
    g.append('text')
      .attr('x', innerW / 2)
      .attr('y', innerH + 38)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text('Time');

    // ------------------------------------------------------------------
    // Y axis (scales, log)
    // ------------------------------------------------------------------
    const yAxis = d3.axisLeft(y)
      .tickValues(scales)
      .tickFormat((d) => d);

    g.append('g')
      .call(yAxis)
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    g.selectAll('.domain').attr('stroke', '#e5e7eb');
    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Y axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerH / 2)
      .attr('y', -44)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text('Scale (scrape intervals)');

    // ------------------------------------------------------------------
    // Color bar legend (below x-axis)
    // ------------------------------------------------------------------
    const colorBarWidth = Math.min(200, innerW * 0.4);
    const colorBarHeight = 8;
    const colorBarX = innerW / 2 - colorBarWidth / 2;
    const colorBarY = innerH + 48;

    const defs = svg.append('defs');
    const gradientId = 'wavelet-color-gradient';
    const gradient = defs.append('linearGradient')
      .attr('id', gradientId)
      .attr('x1', '0%')
      .attr('x2', '100%');

    const nStops = 10;
    for (let i = 0; i <= nStops; i++) {
      const t = i / nStops;
      gradient.append('stop')
        .attr('offset', `${t * 100}%`)
        .attr('stop-color', colorScale(t * powerMax));
    }

    const colorBarG = g.append('g').attr('transform', `translate(${colorBarX}, ${colorBarY})`);
    colorBarG.append('rect')
      .attr('width', colorBarWidth)
      .attr('height', colorBarHeight)
      .attr('fill', `url(#${gradientId})`)
      .attr('rx', 2);

    colorBarG.append('text')
      .attr('x', 0)
      .attr('y', colorBarHeight + 12)
      .attr('text-anchor', 'start')
      .attr('font-size', '9px')
      .attr('fill', '#9ca3af')
      .text('0');

    colorBarG.append('text')
      .attr('x', colorBarWidth)
      .attr('y', colorBarHeight + 12)
      .attr('text-anchor', 'end')
      .attr('font-size', '9px')
      .attr('fill', '#9ca3af')
      .text(powerMax.toFixed(2));

    colorBarG.append('text')
      .attr('x', colorBarWidth / 2)
      .attr('y', colorBarHeight + 12)
      .attr('text-anchor', 'middle')
      .attr('font-size', '9px')
      .attr('fill', '#9ca3af')
      .text('Wavelet Power');

    // ------------------------------------------------------------------
    // Side panel: Global Wavelet Spectrum
    // ------------------------------------------------------------------
    if (globalSpectrum && globalSpectrum.length === nScales) {
      const sideG = svg.append('g')
        .attr('transform', `translate(${sidePanelLeft}, ${MARGIN.top})`);

      const specMax = d3.max(globalSpectrum) || 1;
      const sideX = d3.scaleLinear()
        .domain([0, specMax])
        .range([0, SIDE_PANEL_WIDTH]);

      // Background
      sideG.append('rect')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', SIDE_PANEL_WIDTH)
        .attr('height', innerH)
        .attr('fill', '#fafbfc')
        .attr('stroke', '#e5e7eb')
        .attr('stroke-width', 0.5)
        .attr('rx', 3);

      // Title
      sideG.append('text')
        .attr('x', SIDE_PANEL_WIDTH / 2)
        .attr('y', -6)
        .attr('text-anchor', 'middle')
        .attr('font-size', '9px')
        .attr('font-weight', 600)
        .attr('fill', '#6b7280')
        .text('Global Spectrum');

      // Build line data
      const specPoints = scales.map((s, i) => ({
        y: y(s),
        x: sideX(globalSpectrum[i]),
        scale: s,
        power: globalSpectrum[i],
      }));

      // Area fill
      const specArea = d3.area()
        .x((d) => d.x)
        .x0(0)
        .y((d) => d.y)
        .curve(d3.curveMonotoneY);

      sideG.append('path')
        .datum(specPoints)
        .attr('d', specArea)
        .attr('fill', '#2563eb')
        .attr('fill-opacity', 0.1)
        .attr('stroke', 'none');

      // Line
      const specLine = d3.line()
        .x((d) => d.x)
        .y((d) => d.y)
        .curve(d3.curveMonotoneY);

      sideG.append('path')
        .datum(specPoints)
        .attr('d', specLine)
        .attr('fill', 'none')
        .attr('stroke', '#2563eb')
        .attr('stroke-width', 1.5);

      // Peak marker on side panel
      if (peakScale != null) {
        const peakIdx = scales.indexOf(peakScale);
        if (peakIdx >= 0) {
          const peakPoint = specPoints[peakIdx];
          sideG.append('circle')
            .attr('cx', peakPoint.x)
            .attr('cy', peakPoint.y)
            .attr('r', 3.5)
            .attr('fill', '#2563eb')
            .attr('stroke', '#fff')
            .attr('stroke-width', 1.5);
        }
      }

      // X axis ticks for side panel
      const sideTicks = sideX.ticks(3);
      sideTicks.forEach((t) => {
        sideG.append('line')
          .attr('x1', sideX(t))
          .attr('x2', sideX(t))
          .attr('y1', innerH)
          .attr('y2', innerH + 4)
          .attr('stroke', '#e5e7eb');
        sideG.append('text')
          .attr('x', sideX(t))
          .attr('y', innerH + 14)
          .attr('text-anchor', 'middle')
          .attr('font-size', '8px')
          .attr('fill', '#9ca3af')
          .text(t.toFixed(1));
      });
    }

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // ------------------------------------------------------------------
  // Empty state
  // ------------------------------------------------------------------
  if (!data || !data.dates || !data.scales || !data.power || data.power.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Wavelet Scalogram</div>
        <div style={styles.subtitle}>Time-frequency decomposition of price dynamics</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No wavelet scalogram data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>
        Wavelet Scalogram
        {data.field && (
          <span style={{ fontSize: '12px', fontWeight: 400, color: '#9ca3af', marginLeft: '8px' }}>
            {data.field}
          </span>
        )}
        {data.peakPeriod != null && (
          <span style={styles.peakBadge}>
            Peak period: {data.peakPeriod} intervals
          </span>
        )}
      </div>
      <div style={styles.subtitle}>
        Time-frequency decomposition of price dynamics
      </div>

      <div style={styles.scrollContainer} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <span style={{ color: '#6b7280' }}>
          <span style={{ display: 'inline-block', width: '16px', height: '2px', background: '#fff', border: '1px dashed #999', verticalAlign: 'middle', marginRight: '4px' }} />
          Cone of influence
        </span>
        <span style={{ color: '#6b7280' }}>
          <span style={{ display: 'inline-block', width: '16px', height: '2px', background: '#fbbf24', verticalAlign: 'middle', marginRight: '4px' }} />
          Peak scale
        </span>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {formatDate(tooltip.date)} ({tooltip.date})
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Scale</span>
            <span style={{ fontWeight: 600 }}>{tooltip.scale}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Period</span>
            <span style={{ fontWeight: 600 }}>{tooltip.period} intervals</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            <span style={{ color: '#6b7280' }}>Power</span>
            <span style={{ fontWeight: 600 }}>{tooltip.power.toFixed(4)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
